import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { talents, statuses as statusesApi, stages as stagesApi, users } from '../services/apiService'
import { calculateIsActive } from '../utils/talentStatusUtils'
import TalentForm from '../components/UI/TalentForm'
import { Loader } from '../components/UI/LoadingSpinner'
import StatusTransitionModal from '../components/StatusManagement/StatusTransitionModal'
import StageTransitionModal from '../components/StageManagement/StageTransitionModal'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { formatApiError } from '../utils/formatApiError'

const TalentEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  // Состояние UI
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [talentData, setTalentData] = useState(null)

  // Status transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false)
  const [transitionData, setTransitionData] = useState(null)
  const [statusesList, setStatusesList] = useState([]) // Список всех статусов

  // Stage transition modal state — та же логика перехвата, что у статуса:
  // обычный PUT меняет стадию через хук, но не пишет stage_changed и не
  // требует причины при откате назад. Через форму стадия обязана идти тем же
  // путём, что и из карточки — PATCH /:id/stage.
  const [showStageTransitionModal, setShowStageTransitionModal] = useState(false)
  const [stageTransitionData, setStageTransitionData] = useState(null)
  const [stagesList, setStagesList] = useState([])

  // Данные формы, ожидающие дозаписи после смены статуса и/или стадии —
  // общие для обеих модалок, поскольку изменения могут пойти цепочкой:
  // сперва статус, потом стадия, и только затем остальные поля формы.
  const [pendingFormData, setPendingFormData] = useState(null)

  // Загрузка данных таланта
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('🔍 TalentEdit: Fetching talent for editing, ID:', id)

        const response = await talents.getTalentById(id)

        console.log('🔍 TalentEdit: API response:', response)

        if (response && response.data) {
          if (!hasPermission('talent-service', response.data.talent_type, 'update')) {
            navigate('/unauthorized')
            return
          }
          setTalentData(response.data)
        } else {
          throw new Error('Invalid response structure')
        }
      } catch (err) {
        console.error('Error fetching data:', err)
        setError('Failed to load talent data')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchData()
    }
  }, [id])

  // Загрузка списка статусов для модала
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        if (talentData?.talent_type) {
          const response = await statusesApi.getStatusesByType(talentData.talent_type, { isCommon: 1 })
          if (response && response.data) {
            setStatusesList(response.data)
          }
        }
      } catch (err) {
        console.error('Error fetching statuses:', err)
      }
    }

    if (talentData) {
      fetchStatuses()
    }
  }, [talentData?.talent_type])

  // Загрузка списка стадий для модала — глобальный список, не по типу
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const response = await stagesApi.getStages()
        if (response && response.data) {
          setStagesList(response.data)
        }
      } catch (err) {
        console.error('Error fetching stages:', err)
      }
    }

    if (talentData) {
      fetchStages()
    }
  }, [talentData?.id])

  /**
   * Общее звено цепочки: после того как статус (если менялся) уже
   * разрешился, проверяем стадию относительно актуального состояния таланта
   * (currentTalentData — может быть уже обновлённым после шага статуса).
   * Если стадия тоже меняется — перехватываем через её собственную модалку;
   * если нет — просто дописываем оставшиеся поля формы обычным PUT.
   */
  const proceedAfterStatus = async (formData, currentTalentData) => {
    const stageChanged = currentTalentData.current_stage_id !== formData.current_stage_id

    if (stageChanged) {
      const fromStage = stagesList.find(s => s.id === currentTalentData.current_stage_id)
      const toStage = stagesList.find(s => s.id === formData.current_stage_id)

      if (!toStage) {
        toast.error('Stage information not found')
        throw new Error('Stage information not found')
      }

      setStageTransitionData({
        talentId: id,
        talentName: `${currentTalentData.first_name} ${currentTalentData.last_name}`,
        fromStage: fromStage ? { id: fromStage.id, name: fromStage.name, color: fromStage.color, talent_type: fromStage.talent_type } : null,
        toStage: { id: toStage.id, name: toStage.name, color: toStage.color, talent_type: toStage.talent_type }
      })
      setPendingFormData(formData)
      setShowStageTransitionModal(true)
      return false
    }

    // Ни статус, ни стадия (либо стадия уже не отличается) — обычное сохранение
    const response = await talents.updateTalent(id, formData)
    if (response && response.data) {
      // The success toast is shown by TalentForm once onSubmit returns true
      navigate(`/talents/${id}`)
      return true
    }
    throw new Error('Invalid response structure')
  }

  // Обработка обновления таланта
  const handleUpdateTalent = async (formData) => {
    try {
      console.log('🔍 TalentEdit: Updating talent with data:', formData)

      // Проверяем, изменился ли статус
      const statusChanged = talentData.current_status_id !== formData.current_status_id
      const typeChanged = talentData.talent_type !== formData.talent_type

      if (statusChanged) {
        console.log('🔍 Status changed, showing transition modal')

        // Если тип таланта изменился, нужно загрузить статусы для нового типа
        let currentStatusesList = statusesList

        if (typeChanged) {
          console.log('🔍 Type also changed, fetching statuses for new type:', formData.talent_type)
          try {
            const response = await statusesApi.getStatusesByType(formData.talent_type, { isCommon: 1 })
            if (response && response.data) {
              currentStatusesList = response.data
            }
          } catch (err) {
            console.error('Error fetching statuses for new type:', err)
            toast.error('Failed to load statuses for the new talent type')
            throw new Error('Failed to load statuses for the new talent type')
          }
        }

        // Находим объекты статусов
        const fromStatus = statusesList.find(s => s.id === talentData.current_status_id)
        const toStatus = currentStatusesList.find(s => s.id === formData.current_status_id)

        if (!fromStatus || !toStatus) {
          console.error('Status not found:', {
            fromStatusId: talentData.current_status_id,
            toStatusId: formData.current_status_id,
            availableStatuses: currentStatusesList.map(s => s.id)
          })
          toast.error('Status information not found')
          throw new Error('Status information not found')
        }

        // Подготавливаем данные для модала
        setTransitionData({
          talentId: id,
          talentName: `${talentData.first_name} ${talentData.last_name}`,
          fromStatus: { id: fromStatus.id, name: fromStatus.name, color: fromStatus.color },
          toStatus: { id: toStatus.id, name: toStatus.name, color: toStatus.color }
        })

        // Сохраняем данные формы для последующего обновления
        setPendingFormData(formData)

        // Показываем модал
        setShowTransitionModal(true)

        // Возвращаем false чтобы форма не закрылась
        return false
      }

      // Статус не менялся — проверяем стадию тем же звеном цепочки
      return await proceedAfterStatus(formData, talentData)
    } catch (err) {
      console.error('Error updating talent:', err)
      throw err
    }
  }

  // Обработка успешного изменения статуса
  const handleTransitionSuccess = async (newTalentData) => {
    try {
      console.log('🔍 Status transition successful, updating talent data')

      const newIsActive =
        newTalentData?.is_active !== undefined
          ? newTalentData.is_active
          : calculateIsActive(transitionData?.toStatus)
      const authUserId =
        pendingFormData?.auth_user_id ??
        talentData?.auth_user_id ??
        newTalentData?.auth_user_id
      if (authUserId) {
        users.updateUserActivity(authUserId, newIsActive).catch((err) => {
          console.error('Failed to sync user activity status after status transition:', err)
        })
      }

      // Закрываем модал
      setShowTransitionModal(false)

      // Обновляем данные таланта с новым статусом (и, если хук
      // reconcileStageAndStatus заодно подвинул стадию — с ней тоже), чтобы
      // проверка стадии ниже сравнивалась с актуальным состоянием
      const updatedTalentData = {
        ...talentData,
        current_status_id: transitionData.toStatus.id,
        currentStatus: transitionData.toStatus,
        ...(newTalentData?.current_stage_id !== undefined ? { current_stage_id: newTalentData.current_stage_id } : {}),
        ...(newTalentData?.currentStage !== undefined ? { currentStage: newTalentData.currentStage } : {})
      }
      setTalentData(updatedTalentData)

      // Теперь разбираемся со стадией (если она тоже поменялась) и
      // остальными полями формы
      if (pendingFormData) {
        const finished = await proceedAfterStatus(pendingFormData, updatedTalentData)
        if (finished) {
          toast.success(`Status changed to "${transitionData.toStatus.name}" and data updated`)
          setPendingFormData(null)
          setTransitionData(null)
        }
        // finished === false: стадийная модалка теперь открыта, pendingFormData
        // остаётся выставленным для неё — здесь его не чистим
      } else {
        toast.success(`Status changed to "${transitionData.toStatus.name}"`)
        setPendingFormData(null)
        setTransitionData(null)
        navigate(`/talents/${id}`)
      }
    } catch (err) {
      console.error('Error updating talent after status change:', err)

      if (err.response?.data?.message) {
        toast.error(`Status changed to "${transitionData.toStatus.name}", but data update failed: ${err.response.data.message}`)
      } else {
        toast.error(`Status changed to "${transitionData.toStatus.name}", but data update failed. Please fix the errors and try again.`)
      }

      setPendingFormData(null)
      setTransitionData(null)

      // НЕ делаем navigate - оставляем пользователя на форме для исправления ошибок
    }
  }

  // Обработка ошибки изменения статуса
  const handleTransitionError = (err) => {
    console.error('Error changing status:', err)
    toast.error(formatApiError(err, 'Error changing status'))
  }

  // Обработка успешного изменения стадии
  const handleStageTransitionSuccess = async (newTalentData) => {
    try {
      setShowStageTransitionModal(false)

      const updatedTalentData = {
        ...talentData,
        current_stage_id: stageTransitionData.toStage.id,
        currentStage: stageTransitionData.toStage,
        // Хук мог заодно подобрать другой статус стадии — подхватываем и его
        ...(newTalentData?.current_status_id !== undefined ? { current_status_id: newTalentData.current_status_id } : {}),
        ...(newTalentData?.currentStatus !== undefined ? { currentStatus: newTalentData.currentStatus } : {})
      }
      setTalentData(updatedTalentData)

      if (pendingFormData) {
        // Стадия задаёт тип: после перевода на стадию другого типа тип таланта
        // уже новый, и остальные поля сохраняем с ним
        const formDataToSave = newTalentData?.talent_type
          ? { ...pendingFormData, talent_type: newTalentData.talent_type }
          : pendingFormData
        const response = await talents.updateTalent(id, formDataToSave)
        if (response && response.data) {
          toast.success(`Stage changed to "${stageTransitionData.toStage.name}" and data updated`)
          setPendingFormData(null)
          setStageTransitionData(null)
          navigate(`/talents/${id}`)
        } else {
          throw new Error('Invalid response structure')
        }
      } else {
        toast.success(`Stage changed to "${stageTransitionData.toStage.name}"`)
        setPendingFormData(null)
        setStageTransitionData(null)
        navigate(`/talents/${id}`)
      }
    } catch (err) {
      console.error('Error updating talent after stage change:', err)
      toast.error(formatApiError(err, `Stage changed to "${stageTransitionData?.toStage?.name}", but data update failed. Please fix the errors and try again.`))
      setPendingFormData(null)
      setStageTransitionData(null)
      // НЕ делаем navigate - оставляем пользователя на форме для исправления ошибок
    }
  }

  const handleStageTransitionError = (err) => {
    console.error('Error changing stage:', err)
    toast.error(formatApiError(err, 'Error changing stage'))
  }

  // Обработка отмены
  const handleCancel = () => {
    navigate(`/talents/${id}`)
  }

  if (loading) {
    return (
      <Loader variant="component" size="md" />
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-100 mb-2">Error loading</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => navigate('/talents')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
         Back to list
          </button>
        </div>
      </div>
    )
  }

  if (!talentData) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-100 mb-2">Talent not found</h2>
          <p className="text-gray-300 mb-4">Talent with ID {id} not found</p>
          <button
            onClick={() => navigate('/talents')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to list
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <title>Edit Talent | Talent Service</title>
      <TalentForm
        initialData={talentData}
        onSubmit={handleUpdateTalent}
        onCancel={handleCancel}
        title="Edit talent"
        submitText="Save changes"
        showHeader={true}
      />

      {/* Status Transition Modal */}
      <StatusTransitionModal
        isOpen={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false)
          setTransitionData(null)
          setPendingFormData(null)
        }}
        entityType="talent"
        entityId={transitionData?.talentId}
        entityName={transitionData?.talentName}
        fromStatus={transitionData?.fromStatus}
        toStatus={transitionData?.toStatus}
        onSuccess={handleTransitionSuccess}
        onError={handleTransitionError}
      />

      {/* Stage Transition Modal */}
      <StageTransitionModal
        isOpen={showStageTransitionModal}
        onClose={() => {
          setShowStageTransitionModal(false)
          setStageTransitionData(null)
          setPendingFormData(null)
        }}
        talentId={stageTransitionData?.talentId}
        talentName={stageTransitionData?.talentName}
        fromStage={stageTransitionData?.fromStage}
        toStage={stageTransitionData?.toStage}
        onSuccess={handleStageTransitionSuccess}
        onError={handleStageTransitionError}
      />
    </>
  )
}

export default TalentEdit
